import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ScanLine, Upload } from 'lucide-react';
import { useCompany } from '../context/CompanyContext';
import BulkUpload from '../components/BulkUpload';
import OCRScanner from '../components/OCRScanner';
import PageHeader, { headerButtonClass, headerPrimaryClass } from '../components/layout/PageHeader';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const money = (n) =>
  (n < 0 ? '-' : '') + '$' + Math.abs(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'receivables', label: 'Receivables', to: '/transactions/ar' },
  { id: 'payables', label: 'Payables', to: '/transactions/ap' },
  { id: 'payroll', label: 'Payroll', to: '/payroll' },
  { id: 'reports', label: 'Reports', to: '/financial-reports' },
];

const SectionTitle = ({ children, right }) => (
  <div className="flex items-baseline justify-between pb-2.5 border-b-2 border-[#14213d]">
    <h2 className="text-[15px] font-bold text-[#14213d]">{children}</h2>
    {right}
  </div>
);

const Dashboard = () => {
  const navigate = useNavigate();
  const { companyId, selectedCompany } = useCompany();
  const [data, setData] = useState(null);
  const [failed, setFailed] = useState(false);
  const [showBulkUpload, setShowBulkUpload] = useState(false);
  const [showOCRScanner, setShowOCRScanner] = useState(false);

  useEffect(() => {
    let live = true;
    setData(null);
    setFailed(false);
    const param = companyId ? `?company_id=${encodeURIComponent(companyId)}` : '';
    fetch(`${API_URL}/api/dashboard/overview${param}`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(r.status))))
      .then((d) => live && setData(d))
      .catch(() => live && setFailed(true));
    return () => { live = false; };
  }, [companyId]);

  const goTab = (id) => {
    const tab = TABS.find((t) => t.id === id);
    if (tab?.to) navigate(tab.to);
  };

  const today = new Date();
  const companyName = selectedCompany?.name || 'Your company';
  const subtitle = `${today.getFullYear()} · January 1 to ${today.toLocaleDateString('en-US', { month: 'long', day: 'numeric' })}`;

  const header = (
    <PageHeader
      title={companyName}
      subtitle={subtitle}
      tabs={TABS}
      activeTab="overview"
      onTab={goTab}
      actions={
        <>
          <button type="button" className={headerButtonClass} onClick={() => setShowOCRScanner(true)}>
            <ScanLine className="w-4 h-4" /> Scan documents
          </button>
          <button type="button" className={headerButtonClass} onClick={() => setShowBulkUpload(true)}>
            <Upload className="w-4 h-4" /> Bulk upload
          </button>
          <Link to="/transactions/quick-check" className={headerPrimaryClass}>Write a check</Link>
        </>
      }
    />
  );

  const modals = (
    <>
      <BulkUpload isOpen={showBulkUpload} onClose={() => setShowBulkUpload(false)} />
      <OCRScanner isOpen={showOCRScanner} onClose={() => setShowOCRScanner(false)} />
    </>
  );

  if (failed) {
    return (
      <div>
        {header}
        <div className="max-w-[1240px] mx-auto px-2 text-[#566078]" data-testid="dashboard-error">
          The dashboard could not load this company's figures. Check your connection and refresh the page.
        </div>
        {modals}
      </div>
    );
  }

  if (!data) {
    return (
      <div>
        {header}
        <div className="flex items-center justify-center h-48">
          <div className="w-7 h-7 border-4 border-[#14213d] border-t-transparent rounded-full animate-spin" />
        </div>
        {modals}
      </div>
    );
  }

  const kpis = [
    { label: 'Cash in bank', value: money(data.cash), note: `${data.banks.length} account${data.banks.length === 1 ? '' : 's'}`, tone: 'text-[#566078]' },
    {
      label: 'Owed to you', value: money(data.receivable),
      note: data.attention.late_invoices.count ? `${money(data.attention.late_invoices.amount)} past due` : 'Nothing past due',
      tone: data.attention.late_invoices.count ? 'text-[#a2380f]' : 'text-[#566078]',
    },
    { label: 'You owe', value: money(data.payable), note: 'Open bills', tone: 'text-[#566078]' },
    {
      label: 'Net income, year to date', value: money(data.net_income_ytd),
      note: data.income_ytd ? `${Math.round((data.net_income_ytd / data.income_ytd) * 100)}% of income` : 'No income posted yet',
      tone: data.net_income_ytd >= 0 ? 'text-[#0f6b4b]' : 'text-[#a2380f]',
    },
  ];

  const maxNet = Math.max(1, ...data.net_by_month.map((m) => Math.max(0, m.net)));
  const lastIndex = data.net_by_month.length - 1;

  const a = data.attention;
  const alerts = [
    a.late_invoices.count && { key: 'late', title: `${a.late_invoices.count} invoice${a.late_invoices.count === 1 ? '' : 's'} past due`, note: money(a.late_invoices.amount), action: 'Review', to: '/transactions/ar', bar: '#c4411a' },
    a.pending_checks.count && { key: 'queue', title: `${a.pending_checks.count} check${a.pending_checks.count === 1 ? '' : 's'} waiting to post`, note: `${money(a.pending_checks.amount)} in the posting queue`, action: 'Post', to: '/transactions/posting-queue', bar: '#d9a21b' },
    a.bills_due_this_week.count && { key: 'bills', title: `${a.bills_due_this_week.count} bill${a.bills_due_this_week.count === 1 ? '' : 's'} due this week`, note: money(a.bills_due_this_week.amount), action: 'Pay', to: '/transactions/ap', bar: '#d9a21b' },
  ].filter(Boolean);

  const maxCost = Math.max(1, ...data.top_costs.map((c) => c.amount));

  return (
    <div>
      {header}
      <div className="max-w-[1240px] mx-auto px-2 pb-12 flex flex-col gap-11" data-testid="dashboard">
        {!data.has_books && (
          <div className="rounded-lg bg-[#f1f4fa] px-5 py-4 text-[#14213d]" data-testid="dashboard-no-books">
            <strong>No books for this company yet.</strong> Set up its chart of accounts under{' '}
            <Link className="underline font-semibold" to="/setup/company">Setup</Link>, or bring in existing data with{' '}
            <Link className="underline font-semibold" to="/file/import-all">Import All Existing Data</Link>.
          </div>
        )}

        <div className="grid gap-y-6" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))' }}>
          {kpis.map((k) => (
            <div key={k.label} className="pr-7 mr-7 border-r border-slate-200 last:border-r-0 min-w-0">
              <div className="text-[13px] font-medium text-[#566078]">{k.label}</div>
              <div className="font-mono text-[28px] tracking-tight mt-1" data-testid={`kpi-${k.label}`}>{k.value}</div>
              <div className={`text-[13px] ${k.tone}`}>{k.note}</div>
            </div>
          ))}
        </div>

        <div className="flex flex-wrap gap-x-14 gap-y-11 items-start">
          <div className="flex-[1.7_1_520px] min-w-0 flex flex-col gap-11">
            <div>
              <SectionTitle right={<span className="text-[13px] text-[#566078]">{today.getFullYear()}</span>}>Net income by month</SectionTitle>
              <div className="flex items-end gap-3.5 h-[150px] mt-5 border-b border-slate-300">
                {data.net_by_month.map((m, i) => (
                  <div
                    key={m.month}
                    title={`${m.month}: ${money(m.net)}`}
                    className="flex-1 min-w-0 rounded-t transition-opacity hover:opacity-75"
                    style={{
                      height: `${m.net > 0 ? Math.max(2, (m.net / maxNet) * 100) : 2}%`,
                      background: m.net < 0 ? '#c4411a' : i === lastIndex ? '#a9b4cc' : '#14213d',
                    }}
                  />
                ))}
              </div>
              <div className="flex gap-3.5 pt-1.5">
                {data.net_by_month.map((m) => (
                  <div key={m.month} className="flex-1 min-w-0 text-center text-xs text-[#566078]">{m.month}</div>
                ))}
              </div>
            </div>

            <div>
              <SectionTitle right={<Link to="/view/transactions" className="font-semibold text-[#1c58d6] hover:opacity-70">Open Transaction Browser</Link>}>
                Recent activity
              </SectionTitle>
              {data.recent.length === 0 ? (
                <p className="py-5 text-[#566078]">No checks or bills yet for this company.</p>
              ) : (
                <div className="overflow-x-auto">
                  <div className="min-w-[480px]">
                    <div className="grid grid-cols-[84px_minmax(0,1.6fr)_90px_130px] gap-4 px-2 py-2.5 text-xs font-bold uppercase tracking-wider text-[#566078] border-b border-slate-200">
                      <span>Date</span><span>Name</span><span>Status</span><span className="text-right">Amount</span>
                    </div>
                    {data.recent.map((r, i) => (
                      <div key={i} className="grid grid-cols-[84px_minmax(0,1.6fr)_90px_130px] gap-4 px-2 py-3 items-center border-b border-slate-100 rounded-md transition-colors hover:bg-[#f2f5fa]">
                        <span className="text-[#566078]">{r.date}</span>
                        <span className="font-semibold truncate">{r.what}</span>
                        <span className="text-[#566078] capitalize">{r.status}</span>
                        <span className="text-right font-mono tabular-nums">{money(r.amount)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="flex-[1_1_300px] min-w-0 flex flex-col gap-11">
            <div>
              <SectionTitle>Needs attention</SectionTitle>
              {alerts.length === 0 ? (
                <p className="py-4 text-[#566078]">Nothing needs attention right now.</p>
              ) : (
                alerts.map((al) => (
                  <div key={al.key} className="flex items-center gap-3 px-2 py-3 border-b border-slate-100 rounded-md transition-colors hover:bg-[#f2f5fa]">
                    <span className="w-1 self-stretch rounded-sm flex-none" style={{ background: al.bar }} />
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold">{al.title}</div>
                      <div className="text-[13px] text-[#566078]">{al.note}</div>
                    </div>
                    <Link to={al.to} className="font-semibold text-[#1c58d6] hover:opacity-70">{al.action}</Link>
                  </div>
                ))
              )}
            </div>

            <div>
              <SectionTitle>Biggest costs this year</SectionTitle>
              {data.top_costs.length === 0 ? (
                <p className="py-4 text-[#566078]">No expenses posted yet.</p>
              ) : (
                <div className="flex flex-col gap-4 pt-4">
                  {data.top_costs.map((c) => (
                    <div key={c.name}>
                      <div className="flex justify-between gap-2">
                        <span className="font-semibold truncate">{c.name}</span>
                        <span className="font-mono tabular-nums">{money(c.amount)}</span>
                      </div>
                      <div className="h-1 bg-slate-200 rounded-full mt-1.5">
                        <div className="h-1 bg-[#14213d] rounded-full" style={{ width: `${Math.round((c.amount / maxCost) * 100)}%` }} />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div>
              <SectionTitle>Bank accounts</SectionTitle>
              {data.banks.length === 0 ? (
                <p className="py-4 text-[#566078]">No bank accounts in the chart of accounts.</p>
              ) : (
                data.banks.map((b) => (
                  <div key={b.number} className="flex justify-between gap-2 px-2 py-3 border-b border-slate-100 rounded-md transition-colors hover:bg-[#f2f5fa]">
                    <span><span className="font-mono text-[#566078]">{b.number}</span>&nbsp; {b.name}</span>
                    <span className="font-mono tabular-nums">{money(b.balance)}</span>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
      {modals}
    </div>
  );
};

export default Dashboard;
